import { DOMUtils } from '../../../../utils/dom'
import { IAd } from '../../ad'
import { AdTarget } from '../../targeting/target'
import { IAdServiceAdapter } from '../adapter'

export interface IGPTAd extends IAd {
    slot: any
}

interface IWindow extends Window {
  googletag: any
  deployads: any
}

declare let window: IWindow

export class GPTAdapter implements IAdServiceAdapter {
    public ads: IGPTAd[] = []
    public adURI: string
    public sortableEnabled: boolean
    public lazyLoadConf = {
        fetchMarginPercent: 50,  // 0.5 viewports
        mobileScaling: 2.0,  // double values for mobile
        renderMarginPercent: 25  // 0.25 viewports
    }

    private jsSrc = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js'

    /**
     * Initializes GPT (instantiate global object and queue).
     */
    constructor(loadAdSlots = true) {
        // load GPT JS
        DOMUtils.loadScript(this.jsSrc)

        // setup queue
        window.googletag = window.googletag || {}
        window.googletag.cmd = window.googletag.cmd || []

        // setup queue for sortable
        window.deployads = window.deployads || []

        if(loadAdSlots) {
            // enqueue base setup
            window.googletag.cmd.push(() => {
                window.googletag.pubads().addEventListener('slotOnload', this.slotOnload.bind(this))
                window.googletag.pubads().addEventListener('slotRenderEnded', this.slotRenderEnded.bind(this))
                window.googletag.pubads().enableLazyLoad(this.lazyLoadConf)
                window.googletag.pubads().enableSingleRequest()
            })

        }

    }

    /**
     * Removes registered ads.
     */
    public destroy(ads = this.ads) {
        window.googletag.cmd.push(() => {
            if(this.sortableEnabled) {
                window.deployads.push(() => {
                    window.deployads.gpt.destroySlots(ads.filter((_) => _.slot).map((_) => _.slot))
                })
            } else {
                window.googletag.destroySlots(ads.filter((_) => _.slot).map((_) => _.slot))
            }
        })
        ads.forEach((ad) => {
            ad.destroy()
            this.ads = this.ads.filter((_) => _.id !== ad.id)
        })
    }

    /**
     * Calls `defineSlot` for given ad.
     *
     * @param ad
     */
    public registerAd(ad: IGPTAd): void {
        const id = ad.id

        // store registered ads
        this.ads.push(ad)

        // queue slot definition
        window.googletag.cmd.push(() => {
            // already destroyed
            if (!this.ads.find((_) => _.id === id)) {
                return
            }

            if (ad.isOutOfPage()) {
                ad.slot = window.googletag.defineOutOfPageSlot(this.adURI, ad.id)
            } else {
                ad.slot = window.googletag.defineSlot(this.filterAdUriOnCustomParam(ad), ad.validSizes(), ad.id)
            }
            ad.slot.addService(window.googletag.pubads())

            // add ad-level targeting
            if (ad.targets) {
                [...ad.targets].forEach((target: AdTarget) =>
                    ad.slot.setTargeting(target.key, target.value))
            }
        })
    }

    /**
     * Filter adURI on custom targeting params
     */
    public filterAdUriOnCustomParam(ad: any) {
        let adURI = this.adURI

        ad.targets.forEach((_: any) => {
            if (_.value[0] === 'viafoura') {
                // tslint:disable-next-line: no-magic-numbers
                adURI = this.adURI.replace( this.adURI.split('/')[2] , 'arn-viafoura')
            }
        })
        return adURI
    }

    /**
     * Calls `enableServices` to load ads, then `display` per ad.
     */
    public requestAds(specificAds?: IGPTAd[]): void {
        window.googletag.cmd.push(() => {
            const ads = specificAds || this.ads
            // ads destroyed since this was enqueued
            if (!ads.length) {
                return
            }

            // display method depends on initial load
            if(this.sortableEnabled) {
                this.sortableDisplay(ads)
            } else {
                window.googletag.enableServices()
                if (window.googletag.pubads().isInitialLoadDisabled()) {
                    window.googletag.pubads().refresh(ads.map((_) => _.slot))
                } else {
                    ads.forEach((ad) => window.googletag.display(ad.id))
                }
            }
        })
    }

    /**
     * Sets sortable refresh and display.
     */
    public sortableDisplay(ads: IGPTAd[]): void {
        window.deployads.push(() => window.deployads.gpt.enableServices())
        if (window.googletag.pubads().isInitialLoadDisabled()) {
            window.deployads.push(() =>  window.deployads.gpt.pubadsRefresh(ads.map((_) => _.slot)))
        } else {
            ads.forEach((ad) => window.deployads.push(() => window.deployads.gpt.display(ad.id)))
        }
    }

    /**
     * Sets page-level targeting information.
     */
    public target(targets: AdTarget | AdTarget[]): void {
        window.googletag.cmd.push(() =>
            [].concat(targets).forEach((target) =>
                window.googletag.pubads().setTargeting(target.key, target.value)))
    }

    /**
     * Remove/hide Ads if Empty response.
     *
     * @param e event object from google
     */
    public removeAdOnEmpty(e: any) {
        const id = e.slot.getSlotId().getDomId()
        const el = this.ads.find((ad) => ad.id === id)
        if(!e.isEmpty) {
            el.calculateRefreshSizes(document.querySelector(`#${id}`))
        }
    }

    /**
     * Called when ad creative loaded into iframe.
     *
     * @param e event object from google
     */
    private slotOnload(e: any) {
        const id = e.slot.getSlotId().getDomId()
        this.ads.find((ad) => ad.id === id).loaded()
    }
    /**
     * Handles slot load events. Calls `IAd.display` with acutal size.
     *
     * @param e event object from google
     */
    private slotRenderEnded(e: any) {
        const id = e.slot.getSlotId().getDomId()
        this.removeAdOnEmpty(e)
        this.ads.find((ad) => ad.id === id).display(
            e.isEmpty ? undefined : e.size,
            e.isEmpty ? undefined : e.slot.getResponseInformation().advertiserId)
    }
}
